import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { landingAPI, uploadAPI, schoolProgramAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Skeleton } from '../components/ui/skeleton';
import SmartImage from '../components/SmartImage';
import SocialProofSection from '../components/SocialProofSection';
import SafetySection from '../components/SafetySection';
import ProgramsSection from '../components/ProgramsSection';
import PartnershipsSection from '../components/PartnershipsSection';
import ProgramAudienceSelector from '../components/ProgramAudienceSelector';
import {
  Waves,
  ChevronRight,
  ChevronDown,
  Phone,
  Mail,
  MapPin,
  Shield,
  Award,
  Users,
  Clock,
  CheckCircle,
  Play,
  Download,
  ExternalLink,
  Star,
  Calendar,
  GraduationCap,
  Heart,
  MessageCircle,
  Facebook,
  Instagram,
  Menu,
  X,
  ArrowRight,
  Sparkles,
  TrendingUp,
  FileText,
  Lock,
  Eye,
  UserCheck,
  BookOpen,
  Edit2,
  Loader2,
  Upload,
  Trash2,
  Plus,
  Image,
  Video,
  Save,
  School,
  LifeBuoy,
  BadgeCheck,
  Building2,
  FileCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import { LandingImageUpload, LogoUpload } from '../components/LandingImageUpload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Hero Images
const defaultHeroImages = [
  'https://images.pexels.com/photos/6011942/pexels-photo-6011942.jpeg',
  'https://images.pexels.com/photos/6011945/pexels-photo-6011945.jpeg',
  'https://images.pexels.com/photos/9044082/pexels-photo-9044082.jpeg',
];

// Icon mapping for features
const iconMap = {
  Shield, Award, TrendingUp, FileText, Users, Heart, Clock, Calendar, 
  GraduationCap, Lock, Eye, UserCheck, BookOpen, CheckCircle, Star, MapPin
};

// Role Access Data
const roleAccessData = [
  { role: 'Admin/Owner', level: 100, icon: Lock, description: 'Full system access, user management, settings, certificates approval', color: 'bg-red-500' },
  { role: 'Manager', level: 80, icon: UserCheck, description: 'Manage students, programs, coaches, view all data', color: 'bg-orange-500' },
  { role: 'Coach', level: 50, icon: GraduationCap, description: 'View assigned students, mark attendance, add progress notes', color: 'bg-blue-500' },
  { role: 'Viewer', level: 30, icon: Eye, description: 'Read-only analytics, aggregate statistics, no personal data', color: 'bg-purple-500' },
  { role: 'Parent', level: 20, icon: Heart, description: 'View own children\'s progress, lessons, and certificates', color: 'bg-pink-500' },
  { role: 'Student', level: 10, icon: BookOpen, description: 'View own progress and upcoming lessons', color: 'bg-teal-500' },
];

const LandingPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isAdmin } = useAuth();
  
  const [currentHeroImage, setCurrentHeroImage] = useState(0);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [locationFilter, setLocationFilter] = useState('all');
  
  // Admin edit mode
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Landing config from API
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [schoolsConfig, setSchoolsConfig] = useState(null);
  const [founderConfig, setFounderConfig] = useState(null);
  const [socialProofConfig, setSocialProofConfig] = useState(null);
  const [safetyConfig, setSafetyConfig] = useState(null);
  const [programsConfig, setProgramsConfig] = useState(null);
  
  // Edit dialogs
  const [editingProgram, setEditingProgram] = useState(null);
  const [editingLocation, setEditingLocation] = useState(null);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [editingFeature, setEditingFeature] = useState(null);
  const [editingContact, setEditingContact] = useState(false);
  const [editingHero, setEditingHero] = useState(false);
  const [addingTestimonial, setAddingTestimonial] = useState(false);
  
  // Contact form
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  
  // Refs for smooth scrolling
  const heroRef = useRef(null);
  const programsRef = useRef(null);
  const featuresRef = useRef(null);
  const appRef = useRef(null);
  const testimonialsRef = useRef(null);
  const locationsRef = useRef(null);
  const contactRef = useRef(null);

  // Load landing config
  useEffect(() => {
    loadConfig();
    loadSchoolsConfig();
    loadFounderConfig();
    loadSocialProofConfig();
    loadSafetyConfig();
    loadProgramsConfig();
  }, []);
  
  const loadFounderConfig = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/landing/founder-config`);
      if (res.ok) {
        const data = await res.json();
        setFounderConfig(data);
      }
    } catch (error) {
      console.error('Failed to load founder config:', error);
    }
  };

  const loadProgramsConfig = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/landing/programs-pathways-config`);
      if (res.ok) {
        const data = await res.json();
        setProgramsConfig(data);
      }
    } catch (error) {
      console.error('Failed to load programs config:', error);
    }
  };

  const loadSocialProofConfig = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/landing/social-proof-config`);
      if (res.ok) {
        const data = await res.json();
        setSocialProofConfig(data);
      }
    } catch (error) {
      console.error('Failed to load social proof config:', error);
    }
  };

  const loadSafetyConfig = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/landing/safety-standards-config`);
      if (res.ok) {
        const data = await res.json();
        setSafetyConfig(data);
      }
    } catch (error) {
      console.error('Failed to load safety config:', error);
    }
  };

  const loadSchoolsConfig = async () => {
    try {
      const response = await schoolProgramAPI.getConfig();
      setSchoolsConfig(response.data);
    } catch (error) {
      console.error('Failed to load schools config:', error);
    }
  };

  // Auto-rotate hero images
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentHeroImage((prev) => (prev + 1) % defaultHeroImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Auto-rotate testimonials
  useEffect(() => {
    if (config?.testimonials?.length > 0) {
      const interval = setInterval(() => {
        setCurrentTestimonial((prev) => (prev + 1) % config.testimonials.length);
      }, 6000);
      return () => clearInterval(interval);
    }
  }, [config?.testimonials?.length]);

  const loadConfig = async () => {
    try {
      const res = await landingAPI.getConfig();
      setConfig(res.data);
    } catch (error) {
      console.error('Error loading config:', error);
    } finally {
      setLoading(false);
    }
  };

  const scrollToSection = (ref) => {
    ref.current?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  const openWhatsApp = (customMessage) => {
    const message = customMessage || "Hi! I'm interested in swimming lessons at Eswatini Seals Swim Academy.";
    const phone = config?.contact?.whatsapp?.replace(/\s/g, '') || '26876817515';
    window.open(`https://wa.me/${phone.startsWith('+') ? phone.substring(1) : phone.startsWith('268') ? phone : '268' + phone}?text=${encodeURIComponent(message)}`, '_blank');
  };

  // Production app URL for enrollment deep-links
  const LIVE_APP_URL = process.env.REACT_APP_LIVE_APP_URL || '';
  
  const handleEnroll = (programId, locationId = null) => {
    const enrollPath = `/enroll/${programId}${locationId ? `?location=${locationId}` : ''}`;
    const loginPath = `/login?redirect=${encodeURIComponent(enrollPath)}`;
    
    if (LIVE_APP_URL) {
      // Standalone landing page - link to live app
      window.open(`${LIVE_APP_URL}${loginPath}`, '_blank');
    } else if (isAuthenticated) {
      // Integrated mode - go directly to enrollment
      navigate(enrollPath);
    } else {
      // Integrated mode - redirect to login
      navigate(loginPath);
    }
  };

  // Admin save functions
  const saveProgram = async (programData) => {
    setSaving(true);
    try {
      await landingAPI.updateProgram(programData.id, programData);
      await loadConfig();
      setEditingProgram(null);
      toast.success('Program updated successfully');
    } catch (error) {
      toast.error('Failed to update program');
    } finally {
      setSaving(false);
    }
  };

  const saveLocation = async (locationData) => {
    setSaving(true);
    try {
      await landingAPI.updateLocation(locationData.id, locationData);
      await loadConfig();
      setEditingLocation(null);
      toast.success('Location updated successfully');
    } catch (error) {
      toast.error('Failed to update location');
    } finally {
      setSaving(false);
    }
  };

  const saveTestimonial = async (testimonialData) => {
    setSaving(true);
    try {
      if (addingTestimonial) {
        await landingAPI.addTestimonial(testimonialData);
        setAddingTestimonial(false);
      } else {
        await landingAPI.updateTestimonial(testimonialData.id, testimonialData);
      }
      await loadConfig();
      setEditingTestimonial(null);
      toast.success('Testimonial saved successfully');
    } catch (error) {
      toast.error('Failed to save testimonial');
    } finally {
      setSaving(false);
    }
  };

  const deleteTestimonial = async (id) => {
    if (!window.confirm('Delete this testimonial?')) return;
    try {
      await landingAPI.deleteTestimonial(id);
      await loadConfig();
      toast.success('Testimonial deleted');
    } catch (error) {
      toast.error('Failed to delete testimonial');
    }
  };

  const saveFeature = async (featureData) => {
    setSaving(true);
    try {
      await landingAPI.updateFeature(featureData.id, featureData);
      await loadConfig();
      setEditingFeature(null);
      toast.success('Feature updated successfully');
    } catch (error) {
      toast.error('Failed to update feature');
    } finally {
      setSaving(false);
    }
  };

  const saveContact = async (contactData) => {
    setSaving(true);
    try {
      await landingAPI.updateConfig({ contact: contactData });
      await loadConfig();
      setEditingContact(false);
      toast.success('Contact info updated successfully');
    } catch (error) {
      toast.error('Failed to update contact info');
    } finally {
      setSaving(false);
    }
  };

  const saveHero = async (heroData) => {
    setSaving(true);
    try {
      await landingAPI.updateConfig({ hero: heroData, logo_url: heroData.logo_url });
      await loadConfig();
      setEditingHero(false);
      toast.success('Hero section updated successfully');
    } catch (error) {
      toast.error('Failed to update hero section');
    } finally {
      setSaving(false);
    }
  };

  const filteredPrograms = config?.programs?.filter(program => {
    if (locationFilter === 'all') return true;
    return program.locations?.includes(locationFilter);
  }) || [];

  const isAdminUser = isAuthenticated && isAdmin && isAdmin();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Admin Edit Mode Toggle */}
      {isAdminUser && (
        <div className="fixed bottom-6 right-6 z-50">
          <Button
            onClick={() => setEditMode(!editMode)}
            className={`shadow-lg ${editMode ? 'bg-green-500 hover:bg-green-600' : 'bg-primary hover:bg-primary-600'}`}
          >
            {editMode ? <><CheckCircle className="w-4 h-4 mr-2" /> Exit Edit Mode</> : <><Edit2 className="w-4 h-4 mr-2" /> Edit Page</>}
          </Button>
        </div>
      )}

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => scrollToSection(heroRef)}>
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary-600 flex items-center justify-center">
                {config?.logo_url ? (
                  <img src={config.logo_url} alt="Logo" className="w-8 h-8 object-contain" />
                ) : (
                  <Waves className="w-6 h-6 text-white" />
                )}
              </div>
              <div className="hidden sm:block">
                <h1 className="font-heading font-bold text-primary text-lg leading-tight">{config?.hero?.academy_name || 'Eswatini Seals'}</h1>
                <p className="text-xs text-slate-500">{config?.hero?.academy_subtitle || 'Swim Academy'}</p>
              </div>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-6">
              <button onClick={() => scrollToSection(programsRef)} className="text-slate-600 hover:text-primary transition-colors font-medium">Programs</button>
              <button onClick={() => scrollToSection(featuresRef)} className="text-slate-600 hover:text-primary transition-colors font-medium">Features</button>
              <button onClick={() => scrollToSection(locationsRef)} className="text-slate-600 hover:text-primary transition-colors font-medium">Locations</button>
              <button onClick={() => scrollToSection(testimonialsRef)} className="text-slate-600 hover:text-primary transition-colors font-medium">Testimonials</button>
              <button onClick={() => scrollToSection(contactRef)} className="text-slate-600 hover:text-primary transition-colors font-medium">Contact</button>
            </div>

            {/* CTA Buttons */}
            <div className="hidden md:flex items-center gap-3">
              {isAuthenticated ? (
                <>
                  {isAdminUser && (
                    <Button 
                      onClick={() => setEditMode(!editMode)} 
                      variant="outline"
                      className={`border-primary ${editMode ? 'bg-green-500 text-white hover:bg-green-600' : 'text-primary hover:bg-primary hover:text-white'}`}
                    >
                      <Edit2 className="w-4 h-4 mr-2" />
                      {editMode ? 'Exit Edit' : 'Edit Page'}
                    </Button>
                  )}
                  <Button onClick={() => navigate('/dashboard')} className="bg-primary hover:bg-primary-600">
                    Dashboard
                  </Button>
                </>
              ) : (
                <>
                  <Button 
                    variant="outline" 
                    onClick={() => navigate('/login?redirect=/')} 
                    className="border-primary text-primary hover:bg-primary hover:text-white"
                  >
                    Admin Login
                  </Button>
                  {LIVE_APP_URL && (
                    <Button 
                      onClick={() => window.open(`${LIVE_APP_URL}/login`, '_blank')} 
                      className="bg-primary hover:bg-primary-600"
                    >
                      Sign In to App
                    </Button>
                  )}
                  <Button onClick={() => openWhatsApp()} className="bg-green-500 hover:bg-green-600 text-white">
                    <MessageCircle className="w-4 h-4 mr-2" />
                    WhatsApp
                  </Button>
                </>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t shadow-lg">
            <div className="px-4 py-4 space-y-3">
              <button onClick={() => scrollToSection(programsRef)} className="block w-full text-left py-2 text-slate-600 hover:text-primary">Programs</button>
              <button onClick={() => scrollToSection(featuresRef)} className="block w-full text-left py-2 text-slate-600 hover:text-primary">Features</button>
              <button onClick={() => scrollToSection(locationsRef)} className="block w-full text-left py-2 text-slate-600 hover:text-primary">Locations</button>
              <button onClick={() => scrollToSection(testimonialsRef)} className="block w-full text-left py-2 text-slate-600 hover:text-primary">Testimonials</button>
              <button onClick={() => scrollToSection(contactRef)} className="block w-full text-left py-2 text-slate-600 hover:text-primary">Contact</button>
              <div className="pt-3 border-t space-y-2">
                {isAuthenticated ? (
                  <>
                    {isAdminUser && (
                      <Button 
                        onClick={() => { setEditMode(!editMode); setMobileMenuOpen(false); }} 
                        variant="outline"
                        className={`w-full ${editMode ? 'bg-green-500 text-white' : ''}`}
                      >
                        <Edit2 className="w-4 h-4 mr-2" />
                        {editMode ? 'Exit Edit Mode' : 'Edit Page'}
                      </Button>
                    )}
                    <Button onClick={() => navigate('/dashboard')} className="w-full">Dashboard</Button>
                  </>
                ) : (
                  <div className="flex flex-col gap-2">
                    <Button variant="outline" onClick={() => navigate('/login?redirect=/')} className="w-full">Admin Login</Button>
                    {LIVE_APP_URL && (
                      <Button onClick={() => window.open(`${LIVE_APP_URL}/login`, '_blank')} className="w-full">Sign In to App</Button>
                    )}
                    <Button onClick={() => openWhatsApp()} className="w-full bg-green-500 hover:bg-green-600">WhatsApp</Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section ref={heroRef} className="relative min-h-screen flex items-center pt-16">
        {/* Admin Edit Button for Hero */}
        {editMode && (
          <button
            onClick={() => setEditingHero(true)}
            className="absolute top-20 right-4 z-20 p-3 bg-white/90 backdrop-blur-sm rounded-xl shadow-lg hover:bg-white transition-colors"
          >
            <Edit2 className="w-5 h-5 text-primary" />
          </button>
        )}

        <div className="absolute inset-0 overflow-hidden">
          {defaultHeroImages.map((img, idx) => (
            <div
              key={idx}
              className={`absolute inset-0 transition-opacity duration-1000 ${idx === currentHeroImage ? 'opacity-100' : 'opacity-0'}`}
            >
              <SmartImage 
                src={img} 
                alt="Hero background" 
                height="h-full" 
                className="w-full"
                hoverScale={false}
              />
            </div>
          ))}
          <div className="absolute inset-0 bg-gradient-to-r from-primary-900/95 via-primary-900/80 to-primary-900/60" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-3xl">
            {/* Academy Name & Subtitle - Top of Hero */}
            <div className="mb-6">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white tracking-wide">
                {config?.hero?.academy_name || 'Eswatini Seals'}
              </h2>
              <p className="text-secondary text-lg sm:text-xl font-medium">
                {config?.hero?.academy_subtitle || 'Swim Academy'}
              </p>
            </div>

            <Badge className="mb-6 bg-secondary/20 text-secondary border-secondary/30 text-sm px-4 py-1">
              <Sparkles className="w-4 h-4 mr-2" />
              {config?.hero?.badge_text || "Eswatini's Premier Swim Academy"}
            </Badge>
            
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              {config?.hero?.headline_line1 || 'Drowning Prevention &'}<br />
              <span className="text-secondary">{config?.hero?.headline_line2 || 'Water Safety'}</span>
            </h1>
            
            <p className="text-lg sm:text-xl text-white/80 mb-8 leading-relaxed">
              {config?.hero?.description || 'Developing confident swimmers through professional instruction. Join thousands of families who trust Eswatini Seals for quality swimming education and drowning prevention.'}
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg" 
                onClick={() => scrollToSection(programsRef)}
                className="bg-secondary hover:bg-secondary-600 text-primary-900 font-semibold text-lg px-8 py-6 rounded-xl shadow-lg hover:shadow-xl transition-all"
              >
                {config?.hero?.cta_primary_label || 'Enroll Now'}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                onClick={() => openWhatsApp()}
                className="border-2 border-white text-white hover:bg-white hover:text-primary-900 font-semibold text-lg px-8 py-6 rounded-xl"
              >
                <Phone className="w-5 h-5 mr-2" />
                {config?.hero?.cta_secondary_label || 'Contact Us'}
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 mt-12 pt-8 border-t border-white/20">
              <div>
                <p className="text-3xl sm:text-4xl font-bold text-secondary">{config?.hero?.stats?.students || '500+'}</p>
                <p className="text-white/70 text-sm">{config?.hero?.stats?.students_label || 'Students Trained'}</p>
              </div>
              <div>
                <p className="text-3xl sm:text-4xl font-bold text-secondary">{config?.hero?.stats?.schools || '15+'}</p>
                <p className="text-white/70 text-sm">{config?.hero?.stats?.schools_label || 'Partner Schools'}</p>
              </div>
              <div>
                <p className="text-3xl sm:text-4xl font-bold text-secondary">{config?.hero?.stats?.locations || '4'}</p>
                <p className="text-white/70 text-sm">{config?.hero?.stats?.locations_label || 'Locations'}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex gap-2">
          {defaultHeroImages.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentHeroImage(idx)}
              className={`w-2 h-2 rounded-full transition-all ${idx === currentHeroImage ? 'bg-secondary w-8' : 'bg-white/50'}`}
            />
          ))}
        </div>
      </section>

      {/* Trust signals strip - subtle, non-intrusive */}
      <div className="bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm text-slate-500">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-emerald-500" />
              <span>Safety-First Training</span>
            </div>
            <div className="flex items-center gap-2">
              <Award className="w-4 h-4 text-amber-500" />
              <span>Certified Coaches</span>
            </div>
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-primary" />
              <span>Digital Certificates Issued</span>
            </div>
            <div className="flex items-center gap-2">
              <Heart className="w-4 h-4 text-rose-500" />
              <span>Trusted by Schools & Families</span>
            </div>
          </div>
        </div>
      </div>

      {/* Programs Section */}
      <section ref={programsRef} className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-primary/10 text-primary">Our Programs</Badge>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Swimming Programs for Everyone
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              From holiday intensives to weekly lessons, we have programs designed for all ages and skill levels.
            </p>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            <Badge 
              variant={locationFilter === 'all' ? 'default' : 'outline'}
              className="cursor-pointer px-4 py-2 min-h-[44px] flex items-center"
              onClick={() => setLocationFilter('all')}
            >
              All Locations
            </Badge>
            {['Mbabane', 'Ezulwini', 'Manzini', 'Nhlangano'].map(loc => (
              <Badge 
                key={loc}
                variant={locationFilter === loc ? 'default' : 'outline'}
                className="cursor-pointer px-4 py-2 min-h-[44px] flex items-center"
                onClick={() => setLocationFilter(loc)}
              >
                {loc}
              </Badge>
            ))}
          </div>

          {/* Program Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {filteredPrograms.map((program) => (
              <Card key={program.id} className="group overflow-hidden hover:shadow-xl transition-all duration-300 border-0 relative">
                {editMode && (
                  <button
                    onClick={() => setEditingProgram({...program})}
                    className="absolute top-4 right-4 z-10 p-2 bg-white rounded-lg shadow-md hover:bg-slate-50"
                  >
                    <Edit2 className="w-4 h-4 text-primary" />
                  </button>
                )}
                
                {/* Program Image with lazy-loading, skeleton, and fallback using SmartImage */}
                <SmartImage src={program.image_url} alt={program.name} />
                {program.video_url && program.image_url && (
                  <div className="absolute top-0 left-0 right-0 h-48 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                    <Play className="w-12 h-12 text-white" />
                  </div>
                )}
                
                <div className={`h-3 bg-gradient-to-r ${program.id === 'holiday' ? 'from-blue-500 to-cyan-500' : program.id === 'saturday' ? 'from-teal-500 to-emerald-500' : 'from-purple-500 to-pink-500'}`} />
                <CardContent className="p-6">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${program.id === 'holiday' ? 'from-blue-500 to-cyan-500' : program.id === 'saturday' ? 'from-teal-500 to-emerald-500' : 'from-purple-500 to-pink-500'} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    {program.id === 'holiday' ? <Calendar className="w-7 h-7 text-white" /> : program.id === 'saturday' ? <Clock className="w-7 h-7 text-white" /> : <Users className="w-7 h-7 text-white" />}
                  </div>
                  
                  <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">{program.name}</h3>
                  <p className="text-slate-600 text-sm mb-4">{program.description}</p>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-sm text-slate-500">
                      <Users className="w-4 h-4 mr-2" />
                      {program.ages}
                    </div>
                    <div className="flex items-center text-sm text-slate-500">
                      <Clock className="w-4 h-4 mr-2" />
                      {program.duration} • {program.schedule}
                    </div>
                    <div className="flex items-center text-sm text-slate-500">
                      <MapPin className="w-4 h-4 mr-2" />
                      {program.locations?.join(', ')}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-4">
                    {program.focus?.map((f, i) => (
                      <Badge key={i} variant="secondary" className="text-xs">{f}</Badge>
                    ))}
                  </div>

                  {/* Micro-trust signals row */}
                  <div className="flex flex-wrap items-center gap-3 mb-4 py-2 border-y border-slate-100">
                    <div className="flex items-center text-xs text-slate-500">
                      <Shield className="w-3 h-3 mr-1 text-emerald-500" />
                      <span>Safety-First</span>
                    </div>
                    <div className="flex items-center text-xs text-slate-500">
                      <Award className="w-3 h-3 mr-1 text-amber-500" />
                      <span>Certified Coaches</span>
                    </div>
                    <div className="flex items-center text-xs text-slate-500">
                      <FileText className="w-3 h-3 mr-1 text-primary" />
                      <span>Digital Certificates</span>
                    </div>
                  </div>

                  {/* Flyer Download */}
                  {program.flyer_url && (
                    <a href={program.flyer_url} target="_blank" rel="noopener noreferrer" className="flex items-center text-sm text-primary hover:underline mb-4">
                      <Download className="w-4 h-4 mr-1" />
                      Download Flyer
                    </a>
                  )}

                  <div className="pt-4 border-t">
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold text-primary">{program.fee}</span>
                      {program.id === 'holiday' ? (
                        <Link to="/holiday-program">
                          <Button size="sm" className="min-h-[44px] px-4">
                            Enroll <ChevronRight className="w-4 h-4 ml-1" />
                          </Button>
                        </Link>
                      ) : (
                        <Button onClick={() => handleEnroll(program.id)} size="sm" className="min-h-[44px] px-4">
                          Enroll <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      )}
                    </div>
                    {/* CTA helper text */}
                    <p className="text-xs text-slate-400 mt-2 text-center">
                      Register → Attend Lessons → Track Progress → Receive Certificate
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
            
            {/* Schools Program Card - ENHANCED with Trust Icons, Accordion, and Dual CTAs */}
            {schoolsConfig?.enabled && (
              <Card className="group overflow-hidden hover:shadow-xl transition-all duration-300 border-0 relative">
                {editMode && (
                  <button
                    onClick={() => navigate('/school-registrations')}
                    className="absolute top-4 right-4 z-10 p-2 bg-white rounded-lg shadow-md hover:bg-slate-50"
                  >
                    <Edit2 className="w-4 h-4 text-primary" />
                  </button>
                )}
                
                {/* Program Image with lazy-loading, skeleton, and fallback using SmartImage */}
                <SmartImage 
                  src={schoolsConfig.carousel_images?.[0] || schoolsConfig.image_url} 
                  alt={schoolsConfig.title || 'Schools Program'} 
                />
                
                {/* Top colored bar - Same gradient style as Saturday */}
                <div className="h-3 bg-gradient-to-r from-emerald-500 to-teal-500" />
                
                <CardContent className="p-6">
                  {/* Icon - Same size and placement as Saturday */}
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <School className="w-7 h-7 text-white" />
                  </div>
                  
                  {/* Title - Same typography as Saturday */}
                  <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">
                    {schoolsConfig.title || 'Schools Program'}
                  </h3>
                  
                  {/* TRUST ICON ROW - Slim row with icons + labels */}
                  <div className="flex flex-wrap gap-x-4 gap-y-2 mb-4 py-2 border-y border-slate-100">
                    <div className="flex items-center text-xs text-slate-600">
                      <LifeBuoy className="w-3.5 h-3.5 mr-1 text-emerald-500" />
                      <span>Drowning Prevention</span>
                    </div>
                    <div className="flex items-center text-xs text-slate-600">
                      <BadgeCheck className="w-3.5 h-3.5 mr-1 text-emerald-500" />
                      <span>Certified Coaches</span>
                    </div>
                    <div className="flex items-center text-xs text-slate-600">
                      <Building2 className="w-3.5 h-3.5 mr-1 text-emerald-500" />
                      <span>School-Based Delivery</span>
                    </div>
                    <div className="flex items-center text-xs text-slate-600">
                      <FileCheck className="w-3.5 h-3.5 mr-1 text-emerald-500" />
                      <span>Digital Certificates</span>
                    </div>
                  </div>
                  
                  {/* Description - Same styling as Saturday */}
                  <p className="text-slate-600 text-sm mb-4">
                    {schoolsConfig.description || 'Comprehensive swimming programs designed specifically for educational institutions.'}
                  </p>
                  
                  {/* Metadata rows - Same structure as Saturday */}
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-sm text-slate-500">
                      <Users className="w-4 h-4 mr-2" />
                      All school ages
                    </div>
                    <div className="flex items-center text-sm text-slate-500">
                      <Clock className="w-4 h-4 mr-2" />
                      Flexible scheduling • Term-based
                    </div>
                    <div className="flex items-center text-sm text-slate-500">
                      <MapPin className="w-4 h-4 mr-2" />
                      On-site at your school
                    </div>
                  </div>

                  {/* Highlight chips - Same styling as Saturday */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    <Badge variant="secondary" className="text-xs">Water Safety</Badge>
                    <Badge variant="secondary" className="text-xs">Group Lessons</Badge>
                    <Badge variant="secondary" className="text-xs">Bulk Discounts</Badge>
                  </div>

                  {/* EXPANDABLE ACCORDION - "Why Schools Choose This Program" */}
                  <Accordion type="single" collapsible className="mb-4 -mx-1">
                    <AccordionItem value="why-schools" className="border-0">
                      <AccordionTrigger 
                        className="text-sm font-semibold text-slate-700 hover:no-underline py-3 px-3 min-h-[44px] bg-slate-50 rounded-lg data-[state=open]:rounded-b-none w-full"
                        data-testid="schools-program-accordion-trigger"
                      >
                        Why Schools Choose This Program
                      </AccordionTrigger>
                      <AccordionContent className="bg-slate-50 px-3 rounded-b-lg">
                        <ul className="space-y-2 text-sm text-slate-600">
                          <li className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                            <span>Structured during school hours</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                            <span>Certified & experienced instructors</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                            <span>Builds discipline, confidence & water safety</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                            <span>Digital certificates on completion</span>
                          </li>
                        </ul>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>

                  {/* Footer - Price and DUAL CTA BUTTONS */}
                  <div className="pt-4 border-t">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-2xl font-bold text-primary">E{schoolsConfig.per_student_price}/student</span>
                    </div>
                    
                    {/* Dual CTA Buttons */}
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Link to="/school-registration" className="flex-1">
                        <Button 
                          size="sm" 
                          className="w-full min-h-[44px] bg-emerald-500 hover:bg-emerald-600"
                          data-testid="schools-program-register-btn"
                        >
                          Register School <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      </Link>
                      {schoolsConfig.flyer_url && (
                        <a 
                          href={schoolsConfig.flyer_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex-1"
                        >
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="w-full min-h-[44px] border-emerald-500 text-emerald-600 hover:bg-emerald-50"
                            data-testid="schools-program-flyer-btn"
                          >
                            <Download className="w-4 h-4 mr-1" />
                            View Program Flyer
                          </Button>
                        </a>
                      )}
                    </div>
                    {/* CTA helper text */}
                    <p className="text-xs text-slate-400 mt-3 text-center">
                      Register → Attend Lessons → Track Progress → Receive Certificate
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section ref={featuresRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-secondary/10 text-secondary">Why Choose Us</Badge>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Features & Benefits
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              We combine world-class instruction with modern technology to deliver the best swimming education.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {config?.features?.map((feature, idx) => {
              const IconComponent = iconMap[feature.icon] || Shield;
              const colorClasses = ['bg-red-100 text-red-600', 'bg-amber-100 text-amber-600', 'bg-blue-100 text-blue-600', 'bg-green-100 text-green-600', 'bg-purple-100 text-purple-600', 'bg-pink-100 text-pink-600'];
              
              return (
                <div key={feature.id || idx} className="group p-6 rounded-2xl bg-slate-50 hover:bg-white hover:shadow-xl transition-all duration-300 relative">
                  {editMode && (
                    <button
                      onClick={() => setEditingFeature({...feature})}
                      className="absolute top-4 right-4 p-2 bg-white rounded-lg shadow-md hover:bg-slate-50 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Edit2 className="w-4 h-4 text-primary" />
                    </button>
                  )}
                  
                  {feature.image_url ? (
                    <SmartImage 
                      src={feature.image_url} 
                      alt={feature.title} 
                      height="h-14" 
                      className="w-14 rounded-2xl mb-4"
                      hoverScale={false}
                    />
                  ) : (
                    <div className={`w-14 h-14 rounded-2xl ${colorClasses[idx % 6]} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                      <IconComponent className="w-7 h-7" />
                    </div>
                  )}
                  <h3 className="font-heading text-lg font-bold text-slate-900 mb-2">{feature.title}</h3>
                  <p className="text-slate-600 text-sm">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* App Integration Section */}
      <section ref={appRef} className="py-20 bg-gradient-to-br from-primary-900 via-primary to-primary-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-white/20 text-white border-white/30">Swim Academy App</Badge>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-6">
                Track Progress with Our Management System
              </h2>
              <p className="text-white/80 mb-8">
                Our comprehensive app allows parents, coaches, and administrators to track student progress, 
                manage lessons, and download certificates - all in one place.
              </p>

              <div className="grid grid-cols-2 gap-4 mb-8">
                {roleAccessData.slice(0, 4).map((role, idx) => (
                  <div key={idx} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`w-8 h-8 rounded-lg ${role.color} flex items-center justify-center`}>
                        <role.icon className="w-4 h-4 text-white" />
                      </div>
                      <span className="font-semibold text-white text-sm">{role.role}</span>
                    </div>
                    <p className="text-white/70 text-xs">{role.description}</p>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  onClick={() => {
                    const targetUrl = isAuthenticated && !LIVE_APP_URL ? '/dashboard' : '/login';
                    if (LIVE_APP_URL) {
                      window.open(`${LIVE_APP_URL}${targetUrl}`, '_blank');
                    } else {
                      navigate(targetUrl);
                    }
                  }}
                  className="bg-white text-primary hover:bg-white/90"
                >
                  {isAuthenticated && !LIVE_APP_URL ? 'Go to Dashboard' : 'Access App'}
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
                {(!isAuthenticated || LIVE_APP_URL) && (
                  <Button 
                    size="lg" 
                    variant="outline"
                    onClick={() => {
                      if (LIVE_APP_URL) {
                        window.open(`${LIVE_APP_URL}/register`, '_blank');
                      } else {
                        navigate('/register');
                      }
                    }}
                    className="border-white text-white hover:bg-white/20"
                  >
                    Register as Parent
                  </Button>
                )}
              </div>
            </div>

            {/* App Preview */}
            <div className="relative">
              <div className="bg-white rounded-3xl shadow-2xl p-6 transform hover:scale-105 transition-transform duration-500">
                <div className="flex items-center gap-3 mb-4 pb-4 border-b">
                  <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                    <Waves className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-heading font-bold text-slate-900">Dashboard</h4>
                    <p className="text-xs text-slate-500">Real-time progress tracking</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="p-4 rounded-xl bg-green-50 border border-green-200">
                    <div className="flex items-center gap-2 text-green-700 font-medium text-sm mb-1">
                      <CheckCircle className="w-4 h-4" />
                      Certificate Approved!
                    </div>
                    <p className="text-xs text-green-600">Your Learn to Swim certificate is ready for download.</p>
                  </div>
                  
                  <div className="p-4 rounded-xl bg-blue-50 border border-blue-200">
                    <div className="flex items-center gap-2 text-blue-700 font-medium text-sm mb-1">
                      <Calendar className="w-4 h-4" />
                      Upcoming Lesson
                    </div>
                    <p className="text-xs text-blue-600">Saturday, 10:00 AM - Mbabane Pool</p>
                  </div>

                  <div className="p-4 rounded-xl bg-slate-50">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-slate-600">Progress</span>
                      <span className="font-semibold text-primary">80%</span>
                    </div>
                    <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div className="h-full bg-primary rounded-full" style={{ width: '80%' }} />
                    </div>
                    <p className="text-xs text-slate-500 mt-2">8 of 10 lessons completed</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Certificate Preview Section */}
      <section className="py-20 bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-amber-100 text-amber-700 border-amber-200">Official Recognition</Badge>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Earn Your Certificate
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Upon program completion and admin approval, receive your official Eswatini Seals certificate with QR verification.
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            {/* Certificate Preview Card */}
            <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden border-4 border-teal-100">
              {/* Gold Tier Banner */}
              <div className="bg-gradient-to-r from-yellow-400 via-yellow-500 to-amber-500 text-white py-3 px-6 flex items-center justify-center gap-2">
                <span className="text-2xl">🥇</span>
                <span className="font-bold uppercase tracking-wider">Gold Certificate</span>
              </div>

              <div className="p-8 sm:p-12">
                {/* Academy Header */}
                <div className="text-center mb-8">
                  <div className="flex justify-center items-center gap-4 mb-4">
                    <div className="w-20 h-20 rounded-full bg-teal-100 flex items-center justify-center shadow-lg">
                      {config?.logo_url ? (
                        <img src={config.logo_url} alt="Logo" className="w-14 h-14 object-contain" />
                      ) : (
                        <Award className="w-10 h-10 text-teal-600" />
                      )}
                    </div>
                  </div>
                  <h3 className="font-heading text-xl font-bold text-teal-700">
                    {config?.hero?.academy_name || 'Eswatini Seals'} {config?.hero?.academy_subtitle || 'Swim Academy'}
                  </h3>
                  <p className="text-slate-500 text-sm">Drowning Prevention & Water Safety</p>
                </div>

                {/* Certificate Content */}
                <div className="text-center space-y-4 mb-8">
                  <p className="text-slate-500 uppercase tracking-widest text-sm font-semibold">Certificate of Completion</p>
                  <div className="py-4 border-y border-dashed border-slate-200">
                    <p className="text-slate-500 text-sm">This is to certify that</p>
                    <p className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 my-2">[Student Name]</p>
                    <p className="text-slate-500 text-sm">has successfully completed the</p>
                    <p className="font-heading text-xl font-semibold text-teal-600 mt-2">Learn to Swim - Beginners Program</p>
                  </div>
                </div>

                {/* Details Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center mb-8">
                  <div className="p-3 rounded-xl bg-slate-50">
                    <p className="text-xs text-slate-500 mb-1">Instructor</p>
                    <p className="font-semibold text-sm text-slate-700">[Coach Name]</p>
                  </div>
                  <div className="p-3 rounded-xl bg-slate-50">
                    <p className="text-xs text-slate-500 mb-1">Location</p>
                    <p className="font-semibold text-sm text-slate-700">[Pool Location]</p>
                  </div>
                  <div className="p-3 rounded-xl bg-slate-50">
                    <p className="text-xs text-slate-500 mb-1">Issue Date</p>
                    <p className="font-semibold text-sm text-slate-700">[Date]</p>
                  </div>
                  <div className="p-3 rounded-xl bg-slate-50">
                    <p className="text-xs text-slate-500 mb-1">Certificate ID</p>
                    <p className="font-semibold text-sm text-slate-700 font-mono">[CERT-XXXX]</p>
                  </div>
                </div>

                {/* QR Code Placeholder & Signature */}
                <div className="flex items-end justify-between pt-6 border-t border-dashed">
                  <div className="text-center">
                    <div className="w-20 h-20 bg-slate-100 rounded-lg flex items-center justify-center mb-2">
                      <svg className="w-16 h-16 text-slate-300" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M3 3h6v6H3V3zm2 2v2h2V5H5zm8-2h6v6h-6V3zm2 2v2h2V5h-2zM3 11h6v6H3v-6zm2 2v2h2v-2H5zm8-2h2v2h-2v-2zm4 0h2v2h-2v-2zm-4 4h2v2h-2v-2zm4 0h2v2h-2v-2zm-4 4h6v2h-6v-2z"/>
                      </svg>
                    </div>
                    <p className="text-xs text-slate-400">Scan to Verify</p>
                  </div>
                  <div className="text-right">
                    <div className="w-32 border-b-2 border-slate-300 mb-2"></div>
                    <p className="text-xs text-slate-500">Digital Signature</p>
                    <p className="text-xs text-slate-400 italic">{config?.hero?.academy_name || 'Eswatini Seals'} Swim Academy</p>
                  </div>
                </div>
              </div>

              {/* Subtle Watermark */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-5">
                <Award className="w-64 h-64 text-teal-900" />
              </div>
            </div>

            {/* Tier Badges */}
            <div className="flex justify-center gap-4 mt-8">
              <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-md">
                <span className="text-lg">🥇</span>
                <span className="text-sm font-semibold text-yellow-700">Gold</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-md">
                <span className="text-lg">🥈</span>
                <span className="text-sm font-semibold text-slate-600">Silver</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-md">
                <span className="text-lg">🥉</span>
                <span className="text-sm font-semibold text-orange-700">Bronze</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section ref={testimonialsRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-pink-100 text-pink-700">Testimonials</Badge>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              What Parents Say
            </h2>
            {editMode && (
              <Button 
                onClick={() => { setAddingTestimonial(true); setEditingTestimonial({ id: '', name: '', role: '', text: '', rating: 5, image_url: '' }); }}
                variant="outline"
                className="mt-4"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Testimonial
              </Button>
            )}
          </div>

          <div className="relative max-w-4xl mx-auto">
            <div className="overflow-hidden">
              {config?.testimonials?.map((testimonial, idx) => (
                <div
                  key={testimonial.id || idx}
                  className={`transition-all duration-500 ${idx === currentTestimonial ? 'opacity-100' : 'opacity-0 absolute inset-0'}`}
                >
                  <Card className="bg-slate-50 border-0 relative">
                    {editMode && (
                      <div className="absolute top-4 right-4 flex gap-2">
                        <button onClick={() => setEditingTestimonial({...testimonial})} className="p-2 bg-white rounded-lg shadow-md hover:bg-slate-50">
                          <Edit2 className="w-4 h-4 text-primary" />
                        </button>
                        <button onClick={() => deleteTestimonial(testimonial.id)} className="p-2 bg-white rounded-lg shadow-md hover:bg-red-50">
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    )}
                    <CardContent className="p-8 text-center">
                      <div className="flex justify-center mb-4">
                        {[...Array(testimonial.rating || 5)].map((_, i) => (
                          <Star key={i} className="w-5 h-5 text-amber-400 fill-amber-400" />
                        ))}
                      </div>
                      <p className="text-lg text-slate-700 mb-6 italic">&ldquo;{testimonial.text}&rdquo;</p>
                      <div className="flex items-center justify-center gap-4">
                        {testimonial.image_url ? (
                          <SmartImage 
                            src={testimonial.image_url} 
                            alt={testimonial.name} 
                            height="h-14" 
                            className="w-14 rounded-full"
                            hoverScale={false}
                          />
                        ) : (
                          <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                            <Users className="w-6 h-6 text-primary" />
                          </div>
                        )}
                        <div className="text-left">
                          <p className="font-semibold text-slate-900">{testimonial.name}</p>
                          <p className="text-sm text-slate-500">{testimonial.role}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>

            <div className="flex justify-center gap-2 mt-6">
              {config?.testimonials?.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentTestimonial(idx)}
                  className={`w-2 h-2 rounded-full transition-all ${idx === currentTestimonial ? 'bg-primary w-8' : 'bg-slate-300'}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ========== FOUNDER AUTHORITY SECTION ========== */}
      <section className="py-20 bg-gradient-to-b from-slate-50 to-white border-t border-slate-100 relative">
        {/* Admin Edit Button */}
        {isAdminUser && (
          <Link
            to="/admin/founder"
            className="absolute top-6 right-6 z-10 flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg shadow-md hover:shadow-lg hover:border-primary transition-all text-sm font-medium text-slate-700 hover:text-primary"
            data-testid="edit-founder-homepage-btn"
          >
            <Edit2 className="w-4 h-4" />
            Edit Founder
          </Link>
        )}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* LEFT: Founder Photo */}
            <div className="order-1 lg:order-1">
              <div className="relative">
                {/* Portrait Frame */}
                <div className="relative aspect-[3/4] max-w-md mx-auto lg:mx-0 rounded-2xl overflow-hidden shadow-2xl">
                  {founderConfig?.portrait_url ? (
                    <img
                      src={founderConfig.portrait_url.startsWith('http') ? founderConfig.portrait_url : `${BACKEND_URL}${founderConfig.portrait_url}`}
                      alt={founderConfig?.name || 'Founder'}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900 flex items-center justify-center">
                      <div className="text-center p-8">
                        <div className="w-32 h-32 mx-auto mb-6 rounded-full bg-slate-600/50 flex items-center justify-center border-2 border-slate-500/30">
                          <Users className="w-16 h-16 text-slate-400" />
                        </div>
                        <p className="text-slate-400 text-sm font-medium">Founder Portrait</p>
                        <p className="text-slate-500 text-xs mt-1">Coming Soon</p>
                      </div>
                    </div>
                  )}
                  {/* Decorative corner accents */}
                  <div className="absolute top-4 left-4 w-16 h-16 border-t-2 border-l-2 border-primary/40 rounded-tl-lg" />
                  <div className="absolute bottom-4 right-4 w-16 h-16 border-b-2 border-r-2 border-primary/40 rounded-br-lg" />
                </div>
                {/* Decorative background element */}
                <div className="absolute -z-10 -bottom-6 -right-6 w-full h-full bg-primary/10 rounded-2xl hidden lg:block" />
              </div>
            </div>

            {/* RIGHT: Text Content */}
            <div className="order-2 lg:order-2">
              <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">Leadership</Badge>
              
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
                {founderConfig?.headline || 'Meet the Founder'}
              </h2>
              
              <p className="text-lg sm:text-xl text-primary font-semibold mb-6" data-testid="homepage-founder-subheadline">
                {founderConfig?.subheadline || 'Water Safety Advocate. Swim Coach. Drowning Prevention Leader.'}
              </p>
              
              <div className="prose prose-lg text-slate-600 mb-8">
                <p className="leading-relaxed">
                  {founderConfig?.bio || "Eswatini Seals Swim Academy is led by a passionate water safety practitioner committed to building confident swimmers and reducing drowning across communities. With structured programs, certified systems, and national collaboration, we don't just teach swimming — "}<span className="font-semibold text-slate-800">we build safety</span>.
                </p>
              </div>

              {/* Authority Indicators */}
              <div className="flex flex-wrap gap-4 mb-8">
                {(founderConfig?.authority_badges?.slice(0, 3) || [
                  { label: 'Certified Instructor' },
                  { label: 'Safety Advocate' },
                  { label: 'Community Leader' },
                ]).map((badge, idx) => {
                  const icons = [Award, Shield, Heart];
                  const IconComponent = icons[idx % icons.length];
                  return (
                    <div key={idx} className="flex items-center gap-2 text-sm text-slate-600 bg-slate-100 px-4 py-2 rounded-full">
                      <IconComponent className="w-4 h-4 text-primary" />
                      <span>{badge.label}</span>
                    </div>
                  );
                })}
              </div>
              
              {/* CTA Button */}
              <Link 
                to="/founder"
                className="inline-flex items-center gap-2 px-6 py-3 bg-slate-900 text-white font-semibold rounded-xl hover:bg-slate-800 transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5 group"
              >
                Read Full Story
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ========== SOCIAL PROOF SECTION ========== */}
      <div id="social-proof">
        <SocialProofSection config={socialProofConfig} />
      </div>

      {/* ========== SAFETY & STANDARDS SECTION ========== */}
      <div id="safety">
        <SafetySection config={safetyConfig} />
      </div>

      {/* ========== PROGRAM AUDIENCE SELECTOR ========== */}
      <ProgramAudienceSelector />

      {/* ========== PROGRAMS & PATHWAYS SECTION ========== */}
      <div id="programs-pathways">
        <ProgramsSection config={programsConfig} />
      </div>

      {/* ========== PARTNERSHIPS SECTION ========== */}
      <div id="partnerships">
        <PartnershipsSection />
      </div>

      {/* Locations Section */}
      <section ref={locationsRef} className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-blue-100 text-blue-700">Our Locations</Badge>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Find a Pool Near You
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {config?.locations?.map((location, idx) => (
              <Card key={location.id || idx} className="hover:shadow-xl transition-all duration-300 border-0 overflow-hidden relative">
                {editMode && (
                  <button
                    onClick={() => setEditingLocation({...location})}
                    className="absolute top-4 right-4 z-10 p-2 bg-white rounded-lg shadow-md hover:bg-slate-50"
                  >
                    <Edit2 className="w-4 h-4 text-primary" />
                  </button>
                )}
                <div className="h-2 bg-gradient-to-r from-primary to-secondary" />
                <CardContent className="p-6">
                  <h3 className="font-heading text-lg font-bold text-slate-900 mb-3">{location.name}</h3>
                  
                  <div className="space-y-2 text-sm mb-4">
                    <div className="flex items-start gap-2 text-slate-600">
                      <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>{location.address}</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-600">
                      <Phone className="w-4 h-4 flex-shrink-0" />
                      <span>{location.phone}</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-600">
                      <Clock className="w-4 h-4 flex-shrink-0" />
                      <span>{location.schedule}</span>
                    </div>
                  </div>

                  <div className="mb-4">
                    <p className="text-xs font-medium text-slate-500 mb-2">Programs:</p>
                    <div className="flex flex-wrap gap-1">
                      {location.programs?.map((prog, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">{prog}</Badge>
                      ))}
                    </div>
                  </div>

                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full"
                    onClick={() => location.map_url && window.open(location.map_url, '_blank')}
                  >
                    <MapPin className="w-4 h-4 mr-2" />
                    View on Map
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section ref={contactRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <div className="flex items-center justify-between mb-4">
                <Badge className="bg-green-100 text-green-700">Get in Touch</Badge>
                {editMode && (
                  <button onClick={() => setEditingContact(true)} className="p-2 bg-slate-100 rounded-lg hover:bg-slate-200">
                    <Edit2 className="w-4 h-4 text-primary" />
                  </button>
                )}
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                Contact Us
              </h2>
              <p className="text-slate-600 mb-8">
                Ready to start your swimming journey? Contact us today to enroll or learn more about our programs.
              </p>

              <div className="space-y-6">
                <a 
                  href={`https://wa.me/${(config?.contact?.whatsapp || '26876817515').replace(/\s/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 p-4 rounded-xl bg-green-50 hover:bg-green-100 transition-colors group"
                >
                  <div className="w-12 h-12 rounded-xl bg-green-500 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <MessageCircle className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">WhatsApp</p>
                    <p className="text-green-600">{config?.contact?.whatsapp || '7681 7515'}</p>
                  </div>
                </a>

                <a 
                  href={`tel:${config?.contact?.phone || '+26876817515'}`}
                  className="flex items-center gap-4 p-4 rounded-xl bg-blue-50 hover:bg-blue-100 transition-colors group"
                >
                  <div className="w-12 h-12 rounded-xl bg-blue-500 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Phone</p>
                    <p className="text-blue-600">{config?.contact?.phone || '+268 7681 7515'}</p>
                  </div>
                </a>

                <a 
                  href={`mailto:${config?.contact?.email || 'info@eswatiniseals.com'}`}
                  className="flex items-center gap-4 p-4 rounded-xl bg-purple-50 hover:bg-purple-100 transition-colors group"
                >
                  <div className="w-12 h-12 rounded-xl bg-purple-500 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Email</p>
                    <p className="text-purple-600">{config?.contact?.email || 'info@eswatiniseals.com'}</p>
                  </div>
                </a>
              </div>

              <div className="mt-8">
                <p className="font-semibold text-slate-900 mb-4">Follow Us</p>
                <div className="flex gap-4">
                  <a href={config?.contact?.facebook || 'https://facebook.com'} target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-xl bg-slate-100 hover:bg-blue-100 flex items-center justify-center transition-colors">
                    <Facebook className="w-5 h-5 text-slate-600 hover:text-blue-600" />
                  </a>
                  <a href={config?.contact?.instagram || 'https://instagram.com'} target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-xl bg-slate-100 hover:bg-pink-100 flex items-center justify-center transition-colors">
                    <Instagram className="w-5 h-5 text-slate-600 hover:text-pink-600" />
                  </a>
                  <a href={config?.contact?.tiktok || 'https://tiktok.com'} target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-xl bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors">
                    <svg className="w-5 h-5 text-slate-600" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                    </svg>
                  </a>
                </div>
              </div>
            </div>

            <Card className="border-0 shadow-xl">
              <CardContent className="p-8">
                <h3 className="font-heading text-xl font-bold text-slate-900 mb-6">Send us a Message</h3>
                <form className="space-y-4" onSubmit={(e) => { 
                  e.preventDefault(); 
                  const email = config?.contact?.email || 'sealsswaziland@gmail.com';
                  const subject = encodeURIComponent(`Inquiry from ${contactForm.name}`);
                  const body = encodeURIComponent(`Name: ${contactForm.name}\nEmail: ${contactForm.email}\n\nMessage:\n${contactForm.message}`);
                  window.open(`mailto:${email}?subject=${subject}&body=${body}`, '_blank');
                }}>
                  <div>
                    <Input 
                      placeholder="Your Name"
                      value={contactForm.name}
                      onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                    />
                  </div>
                  <div>
                    <Input 
                      type="email"
                      placeholder="Your Email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                    />
                  </div>
                  <div>
                    <Textarea 
                      placeholder="Your Message"
                      rows={4}
                      value={contactForm.message}
                      onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    />
                  </div>
                  <Button type="submit" className="w-full" size="lg">
                    <Mail className="w-4 h-4 mr-2" />
                    Send Email
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                  {config?.logo_url ? (
                    <img src={config.logo_url} alt="Logo" className="w-6 h-6 object-contain" />
                  ) : (
                    <Waves className="w-6 h-6 text-white" />
                  )}
                </div>
                <div>
                  <h4 className="font-heading font-bold text-lg">{config?.hero?.academy_name || 'Eswatini Seals'}</h4>
                  <p className="text-xs text-slate-400">{config?.hero?.academy_subtitle || 'Swim Academy'}</p>
                </div>
              </div>
              <p className="text-slate-400 text-sm max-w-md">
                Developing confident swimmers through professional instruction. 
                Drowning prevention and water safety for all ages.
              </p>
            </div>

            <div>
              <h5 className="font-semibold mb-4">Quick Links</h5>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><button onClick={() => scrollToSection(programsRef)} className="hover:text-white transition-colors">Programs</button></li>
                <li><button onClick={() => scrollToSection(locationsRef)} className="hover:text-white transition-colors">Locations</button></li>
                <li><button onClick={() => scrollToSection(contactRef)} className="hover:text-white transition-colors">Contact</button></li>
                <li><button onClick={() => navigate('/login')} className="hover:text-white transition-colors">Sign In</button></li>
              </ul>
            </div>

            <div>
              <h5 className="font-semibold mb-4">Contact</h5>
              <ul className="space-y-2 text-sm text-slate-400">
                <li className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  {config?.contact?.phone || '+268 7681 7515'}
                </li>
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  {config?.contact?.email || 'info@eswatiniseals.com'}
                </li>
                <li className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Mbabane, Eswatini
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-slate-400">
              © {new Date().getFullYear()} {config?.hero?.academy_name || 'Eswatini Seals'} {config?.hero?.academy_subtitle || 'Swim Academy'}. All rights reserved.
            </p>
            <div className="flex gap-4">
              <a href={config?.contact?.facebook || 'https://facebook.com'} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-colors" title="Facebook">
                <Facebook className="w-5 h-5" />
              </a>
              <a href={config?.contact?.instagram || 'https://instagram.com'} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-colors" title="Instagram">
                <Instagram className="w-5 h-5" />
              </a>
              <a href={config?.contact?.tiktok || 'https://tiktok.com'} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-colors" title="TikTok">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                </svg>
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Edit Program Dialog */}
      <Dialog open={!!editingProgram} onOpenChange={(open) => !open && setEditingProgram(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Program</DialogTitle>
          </DialogHeader>
          {editingProgram && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Program Name</Label>
                  <Input value={editingProgram.name} onChange={(e) => setEditingProgram({ ...editingProgram, name: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Fee</Label>
                  <Input value={editingProgram.fee} onChange={(e) => setEditingProgram({ ...editingProgram, fee: e.target.value })} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea value={editingProgram.description} onChange={(e) => setEditingProgram({ ...editingProgram, description: e.target.value })} rows={3} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Duration</Label>
                  <Input value={editingProgram.duration} onChange={(e) => setEditingProgram({ ...editingProgram, duration: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Schedule</Label>
                  <Input value={editingProgram.schedule} onChange={(e) => setEditingProgram({ ...editingProgram, schedule: e.target.value })} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Age Group</Label>
                <Input value={editingProgram.ages} onChange={(e) => setEditingProgram({ ...editingProgram, ages: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Locations (comma separated)</Label>
                <Input value={editingProgram.locations?.join(', ')} onChange={(e) => setEditingProgram({ ...editingProgram, locations: e.target.value.split(',').map(l => l.trim()) })} />
              </div>
              <div className="space-y-2">
                <Label>Focus Areas (comma separated)</Label>
                <Input value={editingProgram.focus?.join(', ')} onChange={(e) => setEditingProgram({ ...editingProgram, focus: e.target.value.split(',').map(f => f.trim()) })} />
              </div>
              
              {/* Image Upload */}
              <LandingImageUpload
                currentImageUrl={editingProgram.image_url}
                imageType="program"
                itemId={editingProgram.id}
                onUploadSuccess={(url) => setEditingProgram({ ...editingProgram, image_url: url })}
                label="Program Image"
                aspectRatio="landscape"
              />

              {/* Flyer Upload */}
              <LandingImageUpload
                currentImageUrl={editingProgram.flyer_url}
                imageType="program_flyer"
                itemId={editingProgram.id}
                onUploadSuccess={(url) => setEditingProgram({ ...editingProgram, flyer_url: url })}
                label="Program Flyer (PDF/Image)"
                aspectRatio="portrait"
              />

              <div className="space-y-2">
                <Label>Video URL (optional)</Label>
                <Input value={editingProgram.video_url || ''} onChange={(e) => setEditingProgram({ ...editingProgram, video_url: e.target.value })} placeholder="https://youtube.com/..." />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingProgram(null)}>Cancel</Button>
            <Button onClick={() => saveProgram(editingProgram)} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Location Dialog */}
      <Dialog open={!!editingLocation} onOpenChange={(open) => !open && setEditingLocation(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Location</DialogTitle>
          </DialogHeader>
          {editingLocation && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Location Name</Label>
                <Input value={editingLocation.name} onChange={(e) => setEditingLocation({ ...editingLocation, name: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Address</Label>
                <Input value={editingLocation.address} onChange={(e) => setEditingLocation({ ...editingLocation, address: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input value={editingLocation.phone} onChange={(e) => setEditingLocation({ ...editingLocation, phone: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Schedule</Label>
                <Input value={editingLocation.schedule} onChange={(e) => setEditingLocation({ ...editingLocation, schedule: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Programs (comma separated)</Label>
                <Input value={editingLocation.programs?.join(', ')} onChange={(e) => setEditingLocation({ ...editingLocation, programs: e.target.value.split(',').map(p => p.trim()) })} />
              </div>
              <div className="space-y-2">
                <Label>Map URL</Label>
                <Input value={editingLocation.map_url || ''} onChange={(e) => setEditingLocation({ ...editingLocation, map_url: e.target.value })} placeholder="https://maps.google.com/..." />
              </div>
              
              {/* Location Image Upload */}
              <LandingImageUpload
                currentImageUrl={editingLocation.image_url}
                imageType="location"
                itemId={editingLocation.id}
                onUploadSuccess={(url) => setEditingLocation({ ...editingLocation, image_url: url })}
                label="Location Image"
                aspectRatio="landscape"
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingLocation(null)}>Cancel</Button>
            <Button onClick={() => saveLocation(editingLocation)} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Testimonial Dialog */}
      <Dialog open={!!editingTestimonial} onOpenChange={(open) => { if (!open) { setEditingTestimonial(null); setAddingTestimonial(false); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{addingTestimonial ? 'Add Testimonial' : 'Edit Testimonial'}</DialogTitle>
          </DialogHeader>
          {editingTestimonial && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input value={editingTestimonial.name} onChange={(e) => setEditingTestimonial({ ...editingTestimonial, name: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Role</Label>
                  <Input value={editingTestimonial.role} onChange={(e) => setEditingTestimonial({ ...editingTestimonial, role: e.target.value })} placeholder="e.g., Parent, Adult Learner" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Testimonial Text</Label>
                <Textarea value={editingTestimonial.text} onChange={(e) => setEditingTestimonial({ ...editingTestimonial, text: e.target.value })} rows={4} />
              </div>
              <div className="space-y-2">
                <Label>Rating (1-5)</Label>
                <Input type="number" min="1" max="5" value={editingTestimonial.rating} onChange={(e) => setEditingTestimonial({ ...editingTestimonial, rating: parseInt(e.target.value) || 5 })} />
              </div>
              
              {/* Testimonial Photo Upload */}
              <LandingImageUpload
                currentImageUrl={editingTestimonial.image_url}
                imageType="testimonial"
                itemId={editingTestimonial.id || 'new'}
                onUploadSuccess={(url) => setEditingTestimonial({ ...editingTestimonial, image_url: url })}
                label="Person Photo"
                aspectRatio="square"
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditingTestimonial(null); setAddingTestimonial(false); }}>Cancel</Button>
            <Button onClick={() => saveTestimonial(editingTestimonial)} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Feature Dialog */}
      <Dialog open={!!editingFeature} onOpenChange={(open) => !open && setEditingFeature(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Feature</DialogTitle>
          </DialogHeader>
          {editingFeature && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Title</Label>
                <Input value={editingFeature.title} onChange={(e) => setEditingFeature({ ...editingFeature, title: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea value={editingFeature.description} onChange={(e) => setEditingFeature({ ...editingFeature, description: e.target.value })} rows={3} />
              </div>
              <div className="space-y-2">
                <Label>Icon (Shield, Award, Users, Heart, etc.)</Label>
                <Input value={editingFeature.icon} onChange={(e) => setEditingFeature({ ...editingFeature, icon: e.target.value })} />
                <p className="text-xs text-slate-500">Available: Shield, Award, TrendingUp, FileText, Users, Heart, Clock, Calendar, GraduationCap, Lock, Eye, UserCheck, BookOpen, CheckCircle, Star, MapPin</p>
              </div>
              
              {/* Feature Image Upload */}
              <LandingImageUpload
                currentImageUrl={editingFeature.image_url}
                imageType="feature"
                itemId={editingFeature.id}
                onUploadSuccess={(url) => setEditingFeature({ ...editingFeature, image_url: url })}
                label="Feature Image (optional - replaces icon)"
                aspectRatio="square"
              />

              <div className="space-y-2">
                <Label>Video URL (optional)</Label>
                <Input value={editingFeature.video_url || ''} onChange={(e) => setEditingFeature({ ...editingFeature, video_url: e.target.value })} placeholder="https://youtube.com/..." />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingFeature(null)}>Cancel</Button>
            <Button onClick={() => saveFeature(editingFeature)} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Contact Dialog */}
      <Dialog open={editingContact} onOpenChange={setEditingContact}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Contact Information</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>WhatsApp Number</Label>
              <Input 
                value={config?.contact?.whatsapp || ''} 
                onChange={(e) => setConfig({ ...config, contact: { ...config?.contact, whatsapp: e.target.value } })}
                placeholder="7681 7515"
              />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input 
                value={config?.contact?.phone || ''} 
                onChange={(e) => setConfig({ ...config, contact: { ...config?.contact, phone: e.target.value } })}
                placeholder="+268 7681 7515"
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input 
                value={config?.contact?.email || ''} 
                onChange={(e) => setConfig({ ...config, contact: { ...config?.contact, email: e.target.value } })}
                placeholder="info@eswatiniseals.com"
              />
            </div>
            <div className="space-y-2">
              <Label>Facebook URL</Label>
              <Input 
                value={config?.contact?.facebook || ''} 
                onChange={(e) => setConfig({ ...config, contact: { ...config?.contact, facebook: e.target.value } })}
              />
            </div>
            <div className="space-y-2">
              <Label>Instagram URL</Label>
              <Input 
                value={config?.contact?.instagram || ''} 
                onChange={(e) => setConfig({ ...config, contact: { ...config?.contact, instagram: e.target.value } })}
              />
            </div>
            <div className="space-y-2">
              <Label>TikTok URL</Label>
              <Input 
                value={config?.contact?.tiktok || ''} 
                onChange={(e) => setConfig({ ...config, contact: { ...config?.contact, tiktok: e.target.value } })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingContact(false)}>Cancel</Button>
            <Button onClick={() => saveContact(config?.contact)} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Hero Dialog */}
      <Dialog open={editingHero} onOpenChange={setEditingHero}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Hero Section</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Academy Name & Subtitle */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Academy Name</Label>
                <Input 
                  value={config?.hero?.academy_name || ''} 
                  onChange={(e) => setConfig({ 
                    ...config, 
                    hero: { ...config?.hero, academy_name: e.target.value } 
                  })}
                  placeholder="Eswatini Seals"
                />
              </div>
              <div className="space-y-2">
                <Label>Academy Subtitle</Label>
                <Input 
                  value={config?.hero?.academy_subtitle || ''} 
                  onChange={(e) => setConfig({ 
                    ...config, 
                    hero: { ...config?.hero, academy_subtitle: e.target.value } 
                  })}
                  placeholder="Swim Academy"
                />
              </div>
            </div>

            {/* Badge Text */}
            <div className="space-y-2">
              <Label>Badge Text (above headline)</Label>
              <Input 
                value={config?.hero?.badge_text || ''} 
                onChange={(e) => setConfig({ 
                  ...config, 
                  hero: { ...config?.hero, badge_text: e.target.value } 
                })}
                placeholder="Eswatini's Premier Swim Academy"
              />
            </div>

            {/* Headline */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Headline Line 1</Label>
                <Input 
                  value={config?.hero?.headline_line1 || ''} 
                  onChange={(e) => setConfig({ 
                    ...config, 
                    hero: { ...config?.hero, headline_line1: e.target.value } 
                  })}
                  placeholder="Drowning Prevention &"
                />
              </div>
              <div className="space-y-2">
                <Label>Headline Line 2 (highlighted)</Label>
                <Input 
                  value={config?.hero?.headline_line2 || ''} 
                  onChange={(e) => setConfig({ 
                    ...config, 
                    hero: { ...config?.hero, headline_line2: e.target.value } 
                  })}
                  placeholder="Water Safety"
                />
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label>Description Text</Label>
              <Textarea 
                value={config?.hero?.description || ''} 
                onChange={(e) => setConfig({ 
                  ...config, 
                  hero: { ...config?.hero, description: e.target.value } 
                })}
                placeholder="Developing confident swimmers through professional instruction..."
                rows={3}
              />
            </div>

            {/* CTA Buttons */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Primary Button Label</Label>
                <Input 
                  value={config?.hero?.cta_primary_label || ''} 
                  onChange={(e) => setConfig({ 
                    ...config, 
                    hero: { ...config?.hero, cta_primary_label: e.target.value } 
                  })}
                  placeholder="Enroll Now"
                />
              </div>
              <div className="space-y-2">
                <Label>Secondary Button Label</Label>
                <Input 
                  value={config?.hero?.cta_secondary_label || ''} 
                  onChange={(e) => setConfig({ 
                    ...config, 
                    hero: { ...config?.hero, cta_secondary_label: e.target.value } 
                  })}
                  placeholder="Contact Us"
                />
              </div>
            </div>

            {/* Stats */}
            <div className="border-t pt-4">
              <Label className="text-base font-semibold">Statistics</Label>
              <div className="grid grid-cols-3 gap-4 mt-3">
                <div className="space-y-2">
                  <Label className="text-xs">Students Count</Label>
                  <Input 
                    value={config?.hero?.stats?.students || ''} 
                    onChange={(e) => setConfig({ 
                      ...config, 
                      hero: { 
                        ...config?.hero, 
                        stats: { ...config?.hero?.stats, students: e.target.value } 
                      } 
                    })}
                    placeholder="500+"
                  />
                  <Input 
                    value={config?.hero?.stats?.students_label || ''} 
                    onChange={(e) => setConfig({ 
                      ...config, 
                      hero: { 
                        ...config?.hero, 
                        stats: { ...config?.hero?.stats, students_label: e.target.value } 
                      } 
                    })}
                    placeholder="Students Trained"
                    className="text-xs"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Schools Count</Label>
                  <Input 
                    value={config?.hero?.stats?.schools || ''} 
                    onChange={(e) => setConfig({ 
                      ...config, 
                      hero: { 
                        ...config?.hero, 
                        stats: { ...config?.hero?.stats, schools: e.target.value } 
                      } 
                    })}
                    placeholder="15+"
                  />
                  <Input 
                    value={config?.hero?.stats?.schools_label || ''} 
                    onChange={(e) => setConfig({ 
                      ...config, 
                      hero: { 
                        ...config?.hero, 
                        stats: { ...config?.hero?.stats, schools_label: e.target.value } 
                      } 
                    })}
                    placeholder="Partner Schools"
                    className="text-xs"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Locations Count</Label>
                  <Input 
                    value={config?.hero?.stats?.locations || ''} 
                    onChange={(e) => setConfig({ 
                      ...config, 
                      hero: { 
                        ...config?.hero, 
                        stats: { ...config?.hero?.stats, locations: e.target.value } 
                      } 
                    })}
                    placeholder="4"
                  />
                  <Input 
                    value={config?.hero?.stats?.locations_label || ''} 
                    onChange={(e) => setConfig({ 
                      ...config, 
                      hero: { 
                        ...config?.hero, 
                        stats: { ...config?.hero?.stats, locations_label: e.target.value } 
                      } 
                    })}
                    placeholder="Locations"
                    className="text-xs"
                  />
                </div>
              </div>
            </div>

            {/* Logo Upload */}
            <div className="border-t pt-4">
              <LogoUpload
                currentLogoUrl={config?.logo_url}
                onUploadSuccess={(url) => setConfig({ 
                  ...config, 
                  logo_url: url,
                  hero: { ...config?.hero, logo_url: url }
                })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingHero(false)}>Cancel</Button>
            <Button onClick={() => saveHero({ ...config?.hero, logo_url: config?.logo_url })} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LandingPage;
